import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Printer, ArrowLeft, ShieldCheck, QrCode } from "lucide-react";

const TITLES = {
  birth: { en: "BIRTH CERTIFICATE", hi: "जन्म प्रमाण पत्र" },
  death: { en: "DEATH CERTIFICATE", hi: "मृत्यु प्रमाण पत्र" },
  marriage: { en: "MARRIAGE CERTIFICATE", hi: "विवाह प्रमाण पत्र" },
};

export default function CertificatePrint() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [c, setC] = useState(null);

  useEffect(() => {
    api.get(`/certificates/${id}`).then((r) => setC(r.data));
  }, [id]);

  if (!c) return <div className="p-10 text-slate-500">Loading…</div>;

  const heading = lang === "hi" ? c.panchayat?.name_hi || c.panchayat?.name : c.panchayat?.name;
  const title = TITLES[c.type]?.[lang] || TITLES[c.type]?.en;

  const rows = renderRows(c.type, c.data, t);

  // Simple QR substitute (data uri w/ google chart api)
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=110x110&data=${encodeURIComponent(
    `HSPS-${c.certificate_no}`
  )}`;

  return (
    <div className="min-h-screen py-8 px-4 bg-[#FDFBF7]">
      <div className="max-w-3xl mx-auto">
        <div className="no-print flex items-center justify-between mb-6">
          <Link to="/certificates" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#0A3161]"><ArrowLeft className="w-4 h-4" /> Back</Link>
          <div className="flex items-center gap-2">
            {c.approval_status === "pending" && (
              <span className="px-2.5 py-1 text-[10px] uppercase tracking-wider rounded font-semibold bg-amber-50 text-amber-800 border border-amber-200" data-testid="cert-pending-badge">
                {t("pendingApproval")}
              </span>
            )}
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/certificates/${c.id}/pdf`}
               target="_blank" rel="noreferrer"
               data-testid="cert-pdf-btn"
               className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-[#0A3161] text-[#0A3161]">
              <Printer className="w-4 h-4" /> {t("downloadPdf")}
            </a>
            <button onClick={() => window.print()} data-testid="print-cert-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>
              <Printer className="w-4 h-4" /> {t("print")}
            </button>
          </div>
        </div>

        <div className="print-page bg-white border-2 border-double border-[#0A3161] rounded-md p-12 relative gov-watermark shadow-sm" data-testid="certificate-card">
          <div className="ashoka-stripe absolute top-0 left-0 right-0" />
          <div className="relative z-10">
            {/* Header */}
            <div className="text-center pb-5 border-b border-[#0A3161]/30">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#E87722] font-semibold">{t("govOfHaryana")}</div>
              <div className="font-heading text-base font-bold mt-1 text-[#0A3161]">{t("panchayatiRaj")}</div>
              <div className="mt-3 font-heading text-xl font-semibold text-slate-900">Gram Panchayat — {heading}</div>
              <div className="text-xs text-slate-500 mt-0.5">{c.panchayat?.block}, {c.panchayat?.district}</div>
            </div>

            {/* Title */}
            <div className="text-center my-8">
              <div className="inline-block px-6 py-2 border-2 border-[#E87722] rounded">
                <div className="font-heading text-2xl font-bold tracking-wider text-[#0A3161]">{title}</div>
              </div>
            </div>

            {/* Meta + QR */}
            <div className="grid grid-cols-3 gap-4 text-sm mb-6">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("certificateNo")}</div>
                <div className="font-mono font-bold text-slate-900">{c.certificate_no}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("issueDate")}</div>
                <div className="text-slate-900">{new Date(c.issued_at).toLocaleDateString("en-IN")}</div>
              </div>
              <div className="text-right">
                <img src={qrUrl} alt="QR" className="ml-auto" />
              </div>
            </div>

            {/* Body */}
            <div className="rounded-md border border-slate-200 p-5">
              <div className="text-sm text-slate-700 leading-relaxed mb-4">
                {lang === "hi"
                  ? "यह प्रमाणित किया जाता है कि निम्नलिखित विवरण पंचायत के अभिलेख से मेल खाते हैं:"
                  : "This is to certify that the following particulars match the records of this Gram Panchayat:"}
              </div>

              <table className="w-full text-sm">
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.label} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 pr-4 text-[11px] uppercase tracking-wider text-slate-500 font-semibold w-1/3 align-top">{r.label}</td>
                      <td className="py-2 font-medium text-slate-900">{r.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="mt-10 flex items-end justify-between">
              <div className="text-xs text-slate-500 max-w-xs">
                <div className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#107C10]" /> {t("digitalSignature")}</div>
              </div>
              <div className="text-right">
                <div className="border-t border-slate-400 w-44 pt-1 mt-12 text-xs">{t("secretary")} / {t("sarpanch")}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderRows(type, data, t) {
  if (type === "birth") {
    return [
      { label: t("childName"), value: data.child_name || "—" },
      { label: t("dob"), value: data.dob || "—" },
      { label: t("gender"), value: t(data.gender) || "—" },
      { label: t("placeOfBirth"), value: data.place_of_birth || "—" },
      { label: t("fatherName"), value: data.father_name || "—" },
      { label: t("motherName"), value: data.mother_name || "—" },
      { label: t("address"), value: data.address || "—" },
    ];
  }
  if (type === "death") {
    return [
      { label: t("deceasedName"), value: data.deceased_name || "—" },
      { label: t("dod"), value: data.dod || "—" },
      { label: t("age"), value: data.age || "—" },
      { label: t("gender"), value: t(data.gender) || "—" },
      { label: t("placeOfDeath"), value: data.place_of_death || "—" },
      { label: t("fatherName"), value: data.father_name || "—" },
      { label: t("address"), value: data.address || "—" },
    ];
  }
  if (type === "marriage") {
    return [
      { label: t("husbandName"), value: data.husband_name || "—" },
      { label: t("wifeName"), value: data.wife_name || "—" },
      { label: t("marriageDate"), value: data.marriage_date || "—" },
      { label: t("placeOfMarriage"), value: data.place_of_marriage || "—" },
      { label: t("address"), value: data.address || "—" },
    ];
  }
  return [];
}
